import { useState, useEffect } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function Warranty() {
  const [plans, setPlans] = useState([]);
  const [appliances, setAppliances] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedAppliances, setSelectedAppliances] = useState([]);
  const [step, setStep] = useState(0); // 0=plans, 1=select appliances, 2=success
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [planRes, appRes] = await Promise.all([
          api.get('/warranty/plans'),
          api.get('/appliances?limit=50'),
        ]);
        setPlans(planRes.data.plans);
        setAppliances(appRes.data.appliances);
      } catch (err) {
        console.error('Load warranty data error:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const toggleAppliance = (id) => {
    setSelectedAppliances(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post('/warranty/request', {
        plan: selectedPlan.id,
        applianceIds: selectedAppliances,
      });
      setStep(2);
      toast.success('Quote request submitted!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#06b6d4] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] text-[#475569] mb-[2px]">Service & Support</div>
        <h2 className="text-[22px] font-bold text-[#e2e8f0] tracking-[-0.3px]">Extend Warranty</h2>
        <p className="text-[13px] text-[#64748b] mt-[2px]">Extend coverage for your AIBOX fleet</p>
      </div>

      {/* Step 0: Select plan */}
      {step === 0 && (
        <div className="grid grid-cols-3 gap-4 mt-5">
          {plans.map(plan => {
            const isFeatured = plan.badge === 'RECOMMENDED';
            return (
              <div
                key={plan.id}
                className={`bg-[#0f1623] border rounded-xl p-7 text-center transition-all relative hover:translate-y-[-2px] cursor-pointer ${
                  isFeatured
                    ? 'border-[rgba(6,182,212,0.25)]'
                    : 'border-[rgba(148,163,184,0.06)] hover:border-[rgba(148,163,184,0.10)]'
                }`}
                onClick={() => { setSelectedPlan(plan); setStep(1); }}
              >
                {isFeatured && (
                  <div className="absolute -top-px left-1/2 -translate-x-1/2 bg-[#06b6d4] text-[#060a10] text-[9px] font-bold tracking-[1px] px-[14px] py-[3px] rounded-b-[6px]">
                    RECOMMENDED
                  </div>
                )}
                <div className={`text-[52px] font-bold font-mono leading-none mt-4 mb-[2px] ${isFeatured ? 'text-[#06b6d4]' : 'text-[#e2e8f0]'}`}>
                  {plan.years}
                </div>
                <div className="text-[13px] text-[#475569]">{plan.years === 1 ? 'Year' : 'Years'}</div>
                <div className="text-[16px] font-semibold text-[#e2e8f0] mt-[14px] mb-[6px]">{plan.name}</div>
                <div className="text-[12.5px] text-[#64748b] leading-[1.5] mb-5">{plan.description}</div>
                <button
                  className={`w-full py-[10px] px-4 rounded-lg font-semibold text-[12.5px] transition-all ${
                    isFeatured
                      ? 'bg-[#06b6d4] text-[#060a10] hover:bg-[#0ea5c7]'
                      : 'bg-transparent border border-[rgba(148,163,184,0.10)] text-[#64748b] hover:border-[#06b6d4] hover:text-[#06b6d4]'
                  }`}
                >
                  Request Quote
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Step 1: Select appliances */}
      {step === 1 && selectedPlan && (
        <div className="max-w-2xl">
          <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-xl p-6">
            <h3 className="text-[16px] font-semibold text-[#e2e8f0] mb-1">Select Appliances</h3>
            <p className="text-[12.5px] text-[#64748b] mb-4">
              {selectedPlan.name} ({selectedPlan.years} {selectedPlan.years === 1 ? 'Year' : 'Years'})
            </p>
            <div className="space-y-2">
              {appliances.map(a => (
                <label key={a.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedAppliances.includes(a.id) ? 'bg-[rgba(6,182,212,0.05)] border-[rgba(6,182,212,0.25)]' : 'border-[rgba(148,163,184,0.06)] hover:border-[rgba(148,163,184,0.10)]'
                }`}>
                  <input
                    type="checkbox"
                    checked={selectedAppliances.includes(a.id)}
                    onChange={() => toggleAppliance(a.id)}
                    className="w-4 h-4 rounded border-[#475569] text-[#06b6d4] focus:ring-[#06b6d4] bg-[#0f1623]"
                  />
                  <div className="flex-1">
                    <p className="text-[13px] font-medium text-[#e2e8f0]">{a.unitName}</p>
                    <p className="text-[11px] text-[#475569]">{a.serialNumber} {a.productLine ? `— ${a.productLine.name}` : ''}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                className="px-4 py-2 rounded-lg text-[12.5px] font-semibold bg-transparent border border-[rgba(148,163,184,0.10)] text-[#64748b] hover:border-[#06b6d4] hover:text-[#06b6d4] transition-all"
                onClick={() => { setStep(0); setSelectedAppliances([]); }}
              >
                Back
              </button>
              <button
                className="px-6 py-2 rounded-lg text-[12.5px] font-semibold bg-[#06b6d4] text-[#060a10] hover:bg-[#0ea5c7] transition-all disabled:opacity-50"
                onClick={handleSubmit}
                disabled={selectedAppliances.length === 0 || submitting}
              >
                {submitting ? 'Submitting...' : `Request Quote (${selectedAppliances.length} units)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Success */}
      {step === 2 && (
        <div className="max-w-2xl">
          <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-[rgba(16,185,129,0.1)] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-[#10b981]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-[18px] font-semibold text-[#e2e8f0] mb-2">Quote Request Submitted</h3>
            <p className="text-[13px] text-[#64748b] mb-4">
              Our team will review your request and contact you with pricing details.
            </p>
            <button
              className="px-6 py-2 rounded-lg text-[12.5px] font-semibold bg-[#06b6d4] text-[#060a10] hover:bg-[#0ea5c7] transition-all"
              onClick={() => { setStep(0); setSelectedPlan(null); setSelectedAppliances([]); }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
